import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  CalendarDay,
  Cycle,
  CycleInput,
  Resource,
  ResourceInput,
  Section,
  SectionInput,
  WorkCenter,
  WorkCenterInput,
} from './types';

// Hooks de donnees du referentiel. Un fichier par domaine plus tard si besoin ;
// ici le perimetre tient d'un bloc.

const keys = {
  resources: ['resources'] as const,
  sections: ['sections'] as const,
  workCenters: ['work-centers'] as const,
  cycles: ['cycles'] as const,
  calendar: ['calendar-days'] as const,
};

/** Invalide une liste apres mutation : le refetch garde l'IHM alignee sur la base. */
function invalidate(qc: QueryClient, key: readonly unknown[]) {
  return qc.invalidateQueries({ queryKey: key });
}

// --- Moyens chauds ---

export function useResources() {
  return useQuery({ queryKey: keys.resources, queryFn: () => api.get<Resource[]>('/resources') });
}

export function useSaveResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id?: number; data: ResourceInput }) =>
      input.id
        ? api.put<Resource>(`/resources/${input.id}`, input.data)
        : api.post<Resource>('/resources', input.data),
    onSuccess: () => invalidate(qc, keys.resources),
  });
}

export function useDeleteResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/resources/${id}`),
    onSuccess: () => invalidate(qc, keys.resources),
  });
}

// --- Sections ---

export function useSections() {
  return useQuery({ queryKey: keys.sections, queryFn: () => api.get<Section[]>('/sections') });
}

export function useSaveSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id?: number; data: SectionInput }) =>
      input.id
        ? api.put<Section>(`/sections/${input.id}`, input.data)
        : api.post<Section>('/sections', input.data),
    onSuccess: () => Promise.all([invalidate(qc, keys.sections), invalidate(qc, keys.workCenters)]),
  });
}

export function useDeleteSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/sections/${id}`),
    onSuccess: () => Promise.all([invalidate(qc, keys.sections), invalidate(qc, keys.workCenters)]),
  });
}

// --- Centres de charge ---

export function useWorkCenters() {
  return useQuery({ queryKey: keys.workCenters, queryFn: () => api.get<WorkCenter[]>('/work-centers') });
}

export function useSaveWorkCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id?: number; data: WorkCenterInput }) =>
      input.id
        ? api.put<WorkCenter>(`/work-centers/${input.id}`, input.data)
        : api.post<WorkCenter>('/work-centers', input.data),
    onSuccess: () => invalidate(qc, keys.workCenters),
  });
}

export function useDeleteWorkCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/work-centers/${id}`),
    onSuccess: () => invalidate(qc, keys.workCenters),
  });
}

// --- Cycles ---

export function useCycles() {
  return useQuery({ queryKey: keys.cycles, queryFn: () => api.get<Cycle[]>('/cycles') });
}

export function useSaveCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id?: number; data: CycleInput }) =>
      input.id
        ? api.put<Cycle>(`/cycles/${input.id}`, input.data)
        : api.post<Cycle>('/cycles', input.data),
    onSuccess: () => invalidate(qc, keys.cycles),
  });
}

export function useDeleteCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/cycles/${id}`),
    onSuccess: () => invalidate(qc, keys.cycles),
  });
}

// --- Calendrier ---

export function useCalendarDays() {
  return useQuery({ queryKey: keys.calendar, queryFn: () => api.get<CalendarDay[]>('/calendar-days') });
}
